import { usePaneContext } from "@/components/pane/paneContext.ts";
import { useAuth } from "@/hooks/useAuth.ts";
import { useGrid } from "@/hooks/useGrid.ts";
import { datafeed } from "@/lib/datafeed/df.ts";

import { useDashboard } from "@/lib/store.ts";
import {
  ChartingLibraryWidgetOptions,
  CustomIndicator,
  EntityId,
  IChartingLibraryWidget,
  PineJS,
  ResolutionString,
  SaveLoadChartRecord,
  StudyInputValue,
  widget as Widget,
} from "@tradingView/charting_library";
import { merge } from "lodash";
import { memo, useCallback, useEffect, useMemo, useRef } from "react";
import { useMarketplaceStore } from "@/lib/stores/useMarketplaceStore";
import { getSFIndicators } from "./indicators-access";
import { useUser } from "@/hooks/useUser";
import {
  ICascadeLevelDetails,
  useCascadeLevelsBySymbol,
} from "@/hooks/useCascadeLevelsBySymbol";
import {
  IMarginLevelDetails,
  useMarginLevelsBySymbol,
} from "@/hooks/useMarginLevelsBySymbol";
import { BINANCE_RESOLUTION_MAP } from "@/lib/datafeed/config";
import { saveLoadDomain } from "@/lib/constants";

export interface ISfChartProps {
  symbol?: string;
  interval?: ResolutionString;
  options?: ChartingLibraryWidgetOptions;
  syncTabConfigWithChart?: boolean;
  indicatorTitle?: string;
  indicatorInputs?: Record<string, StudyInputValue>;
  indicatorChart?: boolean;
  containerRef?: React.RefObject<HTMLDivElement>;
}

export const SfChartBase = memo(function SfChartBase({
  symbol,
  interval: _interval,
  options,
  syncTabConfigWithChart = true,
  indicatorTitle,
  indicatorInputs,
  containerRef,
}: ISfChartProps) {
  const nodeId = usePaneContext();
  const { updateTabConfig, getNodeConfig } = useGrid(nodeId);
  const config = getNodeConfig();
  const color = config.color;
  const { user } = useAuth();
  const { user: userDoc } = useUser(user?.uid);
  const { purchasedProductIds } = useMarketplaceStore();

  const interval = _interval ?? config.interval ?? ("1H" as ResolutionString);
  const { colorsToSymbolMap, setSymbolForColor } = useDashboard((state) => ({
    colorsToSymbolMap: state.colorsToSymbolMap,
    setSymbolForColor: state.setSymbolForColor,
  }));

  const nodeSymbol = colorsToSymbolMap[color];

  const id = useMemo(
    () => `chartContainer_${Math.random().toString(36).substring(7)}`,
    [],
  );

  const guestId = useMemo(
    () => `guest-${Math.random().toString(36).substring(2, 10)}`,
    [],
  );

  const indicatorAdded = useRef(false);

  const initialOptions: ChartingLibraryWidgetOptions = useMemo(
    () => ({
      container: id,
      locale: "en",
      library_path: "static/charting_library/charting_library/",
      charts_storage_url: `https://${saveLoadDomain}`,
      charts_storage_api_version: "1.1",
      auto_save_delay: 0.5, // возможно лучше сделать 1сек
      datafeed: datafeed,
      symbol: symbol || nodeSymbol || "BTCUSDT",
      interval: interval,
      autosize: true,
      custom_indicators_getter: (
        PineJS: PineJS,
      ): Promise<CustomIndicator[]> => {
        return Promise.resolve(
          getSFIndicators(
            PineJS,
            purchasedProductIds,
            userDoc?.role || "basic",
          ),
        );
      },
      disabled_features: [
        "popup_hints",
        "header_saveload",
        "display_market_status",
      ],
      enabled_features: [
        "study_templates",
        "hide_left_toolbar_by_default",
        "show_spread_operators",
        "hide_main_series_symbol_from_indicator_legend",
        "hide_price_scale_global_last_bar_value",
        "study_overlay_compare_legend_option",
        "use_localstorage_for_settings",
        "two_character_bar_marks_labels",
      ],

      client_id: "sf-front-v2",
      time_scale: {},
      theme: "dark",
      overrides: {
        // Background
        "paneProperties.background": "#0f0f0f",
        "paneProperties.backgroundType": "solid",
        // Candles
        "mainSeriesProperties.barStyle.upColor": "#3CA64B",
        "mainSeriesProperties.barStyle.downColor": "#B2182C",
        "mainSeriesProperties.candleStyle.upColor": "#3CA64B",
        "mainSeriesProperties.candleStyle.downColor": "#B2182C",
        "mainSeriesProperties.candleStyle.borderUpColor": "#3CA64B",
        "mainSeriesProperties.candleStyle.borderDownColor": "#B2182C",
        "mainSeriesProperties.candleStyle.wickUpColor": "#3CA64B",
        "mainSeriesProperties.candleStyle.wickDownColor": "#B2182C",
        "mainSeriesProperties.hollowCandleStyle.borderUpColor": "#3CA64B",
        "mainSeriesProperties.hollowCandleStyle.borderDownColor": "#B2182C",
        "mainSeriesProperties.hollowCandleStyle.upColor": "#3CA64B",
        "mainSeriesProperties.hollowCandleStyle.downColor": "#B2182C",
        "mainSeriesProperties.hollowCandleStyle.wickUpColor": "#3CA64B",
        "mainSeriesProperties.hollowCandleStyle.wickDownColor": "#B2182C",
        // Lines
        "mainSeriesProperties.lineStyle.color": "#ffffff",
        "mainSeriesProperties.lineWithMarkersStyle.color": "#ffffff",
        "mainSeriesProperties.steplineStyle.color": "#ffffff",
        // Areas
        "mainSeriesProperties.areaStyle.color1": "rgba(13, 157, 219, 0.25)",
        "mainSeriesProperties.areaStyle.color2": "rgba(13, 157, 219, 0.05)",
        "mainSeriesProperties.areaStyle.linecolor": "#ffffff",
        "mainSeriesProperties.hlcAreaStyle.closeLowFillColor":
          "rgba(75, 192, 91, 0.2)",
        "mainSeriesProperties.hlcAreaStyle.highCloseFillColor":
          "rgba(204, 43, 43, 0.2)",
        "mainSeriesProperties.hlcAreaStyle.highLineColor": "#B2182C",
        "mainSeriesProperties.hlcAreaStyle.lowLineColor": "#3CA64B",
        "mainSeriesProperties.baselineStyle.topFillColor1":
          "rgba(75, 192, 91, 0.25)",
        "mainSeriesProperties.baselineStyle.topFillColor2":
          "rgba(75, 192, 91, 0.05)",
        "mainSeriesProperties.baselineStyle.topLineColor": "#3CA64B",
        "mainSeriesProperties.baselineStyle.bottomFillColor1":
          "rgba(204, 43, 43, 0.05)",
        "mainSeriesProperties.baselineStyle.bottomFillColor2":
          "rgba(204, 43, 43, 0.25)",
        "mainSeriesProperties.baselineStyle.bottomLineColor": "#B2182C",
        // Exotic charts
        "mainSeriesProperties.columnStyle.upColor": "rgba(75, 192, 91, 0.5)",
        "mainSeriesProperties.columnStyle.downColor": "rgba(204, 43, 43, 0.5)",
        "mainSeriesProperties.hiloStyle.borderColor": "#3CA64B",
        "mainSeriesProperties.hiloStyle.color": "#3CA64B",
        "mainSeriesProperties.hiloStyle.labelColor": "#3CA64B",
        "mainSeriesProperties.haStyle.borderUpColor": "#3CA64B",
        "mainSeriesProperties.haStyle.borderDownColor": "#B2182C",
        "mainSeriesProperties.haStyle.upColor": "#3CA64B",
        "mainSeriesProperties.haStyle.downColor": "#B2182C",
        "mainSeriesProperties.haStyle.wickUpColor": "#3CA64B",
        "mainSeriesProperties.haStyle.wickDownColor": "#B2182C",
        "mainSeriesProperties.kagiStyle.upColor": "#3CA64B",
        "mainSeriesProperties.kagiStyle.upColorProjection": "#3CA64B",
        "mainSeriesProperties.kagiStyle.downColor": "#B2182C",
        "mainSeriesProperties.kagiStyle.downColorProjection": "#B2182C",
        "mainSeriesProperties.pbStyle.borderUpColor": "#3CA64B",
        "mainSeriesProperties.pbStyle.borderUpColorProjection": "#3CA64B",
        "mainSeriesProperties.pbStyle.borderDownColor": "#B2182C",
        "mainSeriesProperties.pbStyle.borderDownColorProjection": "#B2182C",
        "mainSeriesProperties.pbStyle.upColor": "#3CA64B",
        "mainSeriesProperties.pbStyle.upColorProjection": "#3CA64B",
        "mainSeriesProperties.pbStyle.downColor": "#B2182C",
        "mainSeriesProperties.pbStyle.downColorProjection": "#B2182C",
        "mainSeriesProperties.pnfStyle.upColor": "#3CA64B",
        "mainSeriesProperties.pnfStyle.upColorProjection": "#3CA64B",
        "mainSeriesProperties.pnfStyle.downColor": "#B2182C",
        "mainSeriesProperties.pnfStyle.downColorProjection": "#B2182C",
        "mainSeriesProperties.renkoStyle.borderUpColor": "#3CA64B",
        "mainSeriesProperties.renkoStyle.borderUpColorProjection": "#3CA64B",
        "mainSeriesProperties.renkoStyle.borderDownColor": "#B2182C",
        "mainSeriesProperties.renkoStyle.borderDownColorProjection": "#B2182C",
        "mainSeriesProperties.renkoStyle.upColor": "#3CA64B",
        "mainSeriesProperties.renkoStyle.upColorProjection": "#3CA64B",
        "mainSeriesProperties.renkoStyle.downColor": "#B2182C",
        "mainSeriesProperties.renkoStyle.downColorProjection": "#B2182C",
        "mainSeriesProperties.renkoStyle.wickUpColor": "#3CA64B",
        "mainSeriesProperties.renkoStyle.wickDownColor": "#B2182C",
      },
      studies_overrides: {
        "volume.volume.color.0": "rgba(178, 24, 44, 0.5)",
        "volume.volume.color.1": "rgba(60, 166, 75, 0.5)",
        "volume.volume ma:plot.color": "rgb(255, 255, 255)",
        "volume.volume ma:plot.linewidth": 1,
        "volume.volume ma:plot.display": 1,
        // 'volume profile visible range.histBars2.color.0': 'rgba(178, 24, 44, 0.5)',
        // 'volume profile visible range.histBarsVA.color.1': 'rgba(60, 166, 75, 0.5)',
        // 'volume profile visible range.pocLines.color': 'rgb(255, 255, 255)',
        "volume profile visible range.developing poc.color":
          "rgb(255, 255, 255)",
        "volume profile visible range.developing va high.color":
          "rgb(255, 255, 255)",
        "volume profile visible range.developing va high.linestyle": 2,
        "volume profile visible range.developing va low.color":
          "rgb(255, 255, 255)",
        "volume profile visible range.developing va low.linestyle": 2,
      },
      user_id: user?.uid || guestId,
      // custom_css_url: "/static/charting_library/charting_library/custom.css",
      custom_css_url: "/chart.css",

      debug: false,
    }),
    [
      id,
      symbol,
      nodeSymbol,
      interval,
      user?.uid,
      guestId,
      purchasedProductIds,
      userDoc?.role,
    ],
  );
  const widgetRef = useRef<IChartingLibraryWidget | null>(null);

  useEffect(() => {
    if (syncTabConfigWithChart) {
      widgetRef.current?.onChartReady(async () => {
        const chartFromServer = await getSaveChartDetails(nodeId);

        if (chartFromServer) {
          widgetRef.current?.loadChartFromServer(chartFromServer);
        }
        widgetRef.current?.subscribe("onAutoSaveNeeded", () => {
          widgetRef.current?.save(async () => {
            widgetRef.current?.saveChartToServer(
              () => {},
              () => {},
              { chartName: nodeId },
            );
          });
        });
      });
    }
  }, [nodeId, syncTabConfigWithChart]);

  useEffect(() => {
    localStorage.removeItem("tradingview.current_theme.name");
    if (!widgetRef.current) {
      widgetRef.current = new Widget(merge({}, initialOptions, options));
    }
  }, [initialOptions, options]);

  const handleClick = useCallback(() => {
    if (widgetRef.current) {
      widgetRef.current.startFullscreen();
    }
  }, []);

  useEffect(() => {
    const container = containerRef?.current;
    if (container) {
      container.addEventListener("click", handleClick);
      return () => {
        container.removeEventListener("click", handleClick);
      };
    }
  }, [containerRef, handleClick]);

  async function getSaveChartDetails(
    id: string,
  ): Promise<SaveLoadChartRecord | undefined> {
    return new Promise((r) => {
      widgetRef.current?.getSavedCharts((e: SaveLoadChartRecord[]) => {
        const chart = e.find((e) => e.name === id);
        return r(chart);
      });
    });
  }

  useEffect(() => {
    if (widgetRef.current && symbol) {
      widgetRef.current.onChartReady(async () => {
        widgetRef.current?.chart().setSymbol(symbol, () => {});
      });
    }
  }, [color, symbol, id]);

  useEffect(() => {
    if (widgetRef.current && !symbol) {
      widgetRef.current.onChartReady(() => {
        const symbol = nodeSymbol ?? "BTCUSDT";
        widgetRef.current?.chart().setSymbol(symbol, () => {});
        // setSymbolForColor(color, symbol);
      });
    }
  }, [color, nodeSymbol, symbol]);

  useEffect(() => {
    widgetRef.current?.onChartReady(() => {
      widgetRef.current?.applyOverrides(initialOptions?.overrides ?? {});
      widgetRef.current?.chart().getTimeScale().setRightOffset(0);
    });
  }, [initialOptions?.overrides]);

  useEffect(() => {
    if (widgetRef.current && interval) {
      widgetRef.current.onChartReady(() => {
        if (widgetRef.current?.chart) {
          widgetRef.current?.chart().setResolution(interval, () => {});
        }
      });
    }
  }, [interval]);

  const cascadeLevelsRef = useRef<ICascadeLevelDetails[] | null>(null);
  const marginLevelsRef = useRef<IMarginLevelDetails[] | null>(null);
  const marginLevelsEntitiesRef = useRef<EntityId[] | null>(null);
  const cascadeLevelsEntitiesRef = useRef<EntityId[] | null>(null);

  const cascadeLevels = useCascadeLevelsBySymbol(
    BINANCE_RESOLUTION_MAP[interval] || "1m",
    nodeSymbol,
  );

  const marginLevels = useMarginLevelsBySymbol(nodeSymbol);

  useEffect(() => {
    if (cascadeLevels.data) {
      cascadeLevelsRef.current = cascadeLevels.data;
    }
    if (marginLevels.data) {
      marginLevelsRef.current = marginLevels.data;
    }
  }, [cascadeLevels.data, marginLevels.data]);

  const drawCascadeLevels = useCallback(() => {
    if (!cascadeLevelsRef.current) {
      return;
    }
    cascadeLevelsEntitiesRef.current?.map((shape) => {
      widgetRef.current?.chart().removeEntity(shape);
    });
    cascadeLevelsEntitiesRef.current = [];

    cascadeLevelsRef.current?.map(
      async (level) => {
        if (cascadeLevelsEntitiesRef.current && widgetRef.current) {
          const shape = await widgetRef.current
            .chart()
            .createMultipointShape(
              [{ time: level.timestamp / 1000, price: level.price }],
              {
                shape: "horizontal_ray",
                text: level.logicFc
                  ? `⇛ ${level.fcSum.toString()}`
                  : level.fcSum.toString(),
                lock: false,
                overrides: {
                  fontsize: level.logicFc ? 20 : 12,
                  linewidth: 2,
                  linecolor: level.side === "UP" ? "#B2182C" : "#3CA64B",
                  linestyle: 0,
                  showLabel: true,
                  textcolor: "#ffffff",
                  bold: false,
                  vertLabelsAlign: "bottom",
                  horzLabelsAlign: "right",
                  showPrice: true,
                },
              },
            );
          if (shape) {
            cascadeLevelsEntitiesRef.current.push(shape);
          }
        }
      },
      [cascadeLevelsRef, cascadeLevelsEntitiesRef, widgetRef],
    );
  }, []);

  const drawMarginLevels = useCallback(() => {
    if (!marginLevelsRef.current) {
      return;
    }
    const formatter = new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    });
    marginLevelsEntitiesRef.current?.map((shape) => {
      widgetRef.current?.chart().removeEntity(shape);
    });
    marginLevelsEntitiesRef.current = [];
    marginLevelsRef.current?.map(async (level) => {
      if (marginLevelsEntitiesRef.current && widgetRef.current) {
        const shape = await widgetRef.current
          ?.chart()
          .createMultipointShape(
            [{ time: level.timestamp / 1000, price: level.price }],
            {
              shape: "horizontal_ray",
              lock: false,
              text: formatter.format(level.quoteQuantity),
              overrides: {
                fontsize: 12,
                linewidth: 2,
                linecolor: level.side === "UP" ? "#3CA64B" : "#B2182C",
                linestyle: 0,
                showLabel: true,
                textcolor: "#ffffff",
                bold: false,
                vertLabelsAlign: "bottom",
                horzLabelsAlign: "right",
                showPrice: true,
              },
            },
          );
        if (shape) {
          marginLevelsEntitiesRef.current.push(shape);
        }
      }
    });
  }, [marginLevelsRef, marginLevelsEntitiesRef, widgetRef]);

  function clearMarginLevels() {
    if (marginLevelsEntitiesRef.current) {
      marginLevelsEntitiesRef.current.map((shape) => {
        widgetRef.current?.chart().removeEntity(shape);
      });
      marginLevelsEntitiesRef.current = [];
    }
  }

  function clearCascadeLevels() {
    if (cascadeLevelsEntitiesRef.current) {
      cascadeLevelsEntitiesRef.current.map((shape) => {
        widgetRef.current?.chart().removeEntity(shape);
      });
      cascadeLevelsEntitiesRef.current = [];
    }
  }

  useEffect(() => {
    if (widgetRef.current && indicatorTitle && !indicatorAdded.current) {
      indicatorAdded.current = true;
      widgetRef.current.onChartReady(() => {
        switch (indicatorTitle) {
          case "Predict Liquidations":
            clearMarginLevels();
            drawMarginLevels();
            break;
          case "Cascade Levels":
            clearCascadeLevels();
            drawCascadeLevels();
            break;
          case "Formations":
            console.log("Formations");
            // clearFormations();
            // drawFormations();
            break;
          default:
            // console.log("Default");
            widgetRef.current
              ?.chart()
              .createStudy(indicatorTitle, false, false, indicatorInputs)
              .then((e) => {
                if (e && config.indicatorMergedWithChart) {
                  widgetRef.current?.chart().getStudyById(e).mergeUp();
                }
              });
        }
      });
    }
  }, [
    config.indicatorMergedWithChart,
    indicatorTitle,
    indicatorInputs,
    drawMarginLevels,
    drawCascadeLevels,
  ]);

  useEffect(() => {
    if (syncTabConfigWithChart) {
      if (widgetRef.current) {
        widgetRef.current?.onChartReady(() => {
          widgetRef.current
            ?.chart()
            .onSymbolChanged()
            .subscribe(null, () => {
              let symbol = widgetRef.current?.chart().symbol();
              if (symbol?.indexOf("Binance:") === 0) {
                symbol = symbol.replace("Binance:", "");
              }
              if (symbol) {
                setSymbolForColor(color, symbol);
              }
            });
          widgetRef.current
            ?.chart()
            .onIntervalChanged()
            .subscribe(null, (interval) => {
              updateTabConfig({ interval });
            });
        });
        return () => {
          // widgetRef.current?.chart().onSymbolChanged().unsubscribeAll(null);
        };
      }
    }
  }, [color, setSymbolForColor, syncTabConfigWithChart, updateTabConfig]);

  return <div id={id} className={"h-full"} />;
});
